import { Capacitor } from "@capacitor/core";
import { noInput, silentOutput, type VoiceInput, type VoiceOutput } from "./types.js";
import { CloudVoiceInput, type CloudSttTarget } from "./cloud.js";
import { WebVoiceInput, WebVoiceOutput } from "./web.js";

export * from "./types.js";

export function isNative(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/**
 * Native plugins on Android, Web Speech API elsewhere, text-only fallback when neither exists.
 * With `cloud` (opt-in in Settings) speech recognition goes to Whisper on the owner's Worker instead.
 */
export async function createVoice(
  cloud?: () => CloudSttTarget,
): Promise<{ input: VoiceInput; output: VoiceOutput }> {
  const cloudInput = cloud ? new CloudVoiceInput(cloud) : null;
  if (isNative()) {
    const m = await import("./native.js");
    const output = new m.NativeVoiceOutput();
    if (cloudInput) return { input: cloudInput, output };
    const input = new m.NativeVoiceInput();
    // Ask for the microphone at startup, not in the middle of the first question.
    void input.available();
    return { input, output };
  }
  const output = typeof speechSynthesis !== "undefined" ? new WebVoiceOutput() : silentOutput;
  if (cloudInput && (await cloudInput.available())) return { input: cloudInput, output };
  const input = new WebVoiceInput();
  return { input: (await input.available()) ? input : noInput, output };
}
